import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import PropTypes from 'prop-types';
import { system, variant } from 'styled-system';

import sx from '../../../internal/styles/sx';
import type { DrawerBottomSheetContainerProps, State } from './types';

const StyledBottomSheetContainer = styled('div', {
  shouldForwardProp,
})<DrawerBottomSheetContainerProps>(
  system({
    transitionDuration: true,
  }),
  css({
    overflow: 'hidden',
    width: '100%',
    position: 'fixed',
    bottom: 0,
    left: 0,
    fontFamily: 'body',
    bg: 'white',
    zIndex: 'modal',
    display: 'flex',
    flexFlow: 'column',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  }),
  variant({
    prop: 'state',
    variants: {
      opening: {
        opacity: 1,
        transform: 'translate3d(0,100%,0)',
        transitionTimingFunction: 'ease-in',
      },
      opened: {
        opacity: 1,
        transform: 'translate3d(0,0%,0)',
      },
      closing: {
        opacity: 1,
        transform: 'translate3d(0,100%,0)',
        transitionTimingFunction: 'ease-in',
      },
      closed: {
        opacity: 0,
      },
    },
  }),
  variant({
    prop: 'fullHeight',
    variants: {
      true: {
        height: '100%',
        maxHeight: '-webkit-fill-available',
      },
      false: {
        maxHeight: '85%',
      },
    },
  }),
  sx
);

const DrawerBottomSheetContainer = forwardRef<
  HTMLDivElement,
  DrawerBottomSheetContainerProps
>(({ sx, children, state, transitionDuration, fullHeight = false }, ref) => {
  return (
    <StyledBottomSheetContainer
      role="dialog"
      aria-modal="true"
      state={state}
      transitionDuration={transitionDuration}
      fullHeight={fullHeight}
      sx={sx}
      ref={ref}
    >
      {children}
    </StyledBottomSheetContainer>
  );
});

const states: Array<State> = ['opening', 'opened', 'closing', 'closed'];

DrawerBottomSheetContainer.propTypes = {
  children: PropTypes.any.isRequired,
  state: PropTypes.oneOf(states).isRequired,
  fullHeight: PropTypes.bool,
  transitionDuration: PropTypes.string.isRequired,
  sx: PropTypes.object,
};

export default DrawerBottomSheetContainer;
